// Holiday Entitlement Calculator — pure computation + validation core (Epic 29).
//
// This module is the SINGLE source of truth for the web client's holiday math.
// It is intentionally framework-free (no React, no AntD) so the logic can be
// unit-tested in isolation (Story 29.6) and so it stays in lockstep with the
// mobile Dart twin (`meditimex_mobile/.../domain/holiday_calculator.dart`).
// The two implementations share the same function names, inputs, outputs, and
// the same case table — see `29-1-holiday-calculator-shared-spec.md`.
//
// Period row shape:
//   { fromMonth: 1–12, toMonth: 1–12, mode: 'annualEquivalent' | 'directDays', value: number }
//   monthsInRange = toMonth - fromMonth + 1   (a row with toMonth < fromMonth is invalid)

export const ANNUAL_EQUIVALENT = 'annualEquivalent';
export const DIRECT_DAYS = 'directDays';

/**
 * Number of months a row spans, inclusive. Returns 0 for an invalid row
 * (toMonth < fromMonth) so it contributes nothing rather than going negative.
 */
function monthsInRange(row) {
  const span = row.toMonth - row.fromMonth + 1;
  return span > 0 ? span : 0;
}

/**
 * Days contributed by a single period row.
 * - annualEquivalent: value / 12 * monthsInRange (blend an annual rate over the span).
 * - directDays:       value as-is.
 * Negative `value` is treated as 0 (per spec AC#7). Fractional values are preserved.
 */
export function daysForPeriod(row) {
  const value = row.value > 0 ? row.value : 0;
  if (row.mode === ANNUAL_EQUIVALENT) {
    return (value / 12) * monthsInRange(row);
  }
  // directDays (default for any non-annualEquivalent mode)
  return value;
}

/**
 * Sum of every row's days. With `round` ON (the default) the GRAND TOTAL is
 * rounded to the nearest 0.5 day, half-up (round(total * 2) / 2) — applied once
 * to the total, never per-row, to avoid compounding rounding error. With
 * `round` OFF the exact fractional total is returned.
 */
export function totalDays(rows, { round = true } = {}) {
  const total = rows.reduce((sum, row) => sum + daysForPeriod(row), 0);
  return round ? Math.round(total * 2) / 2 : total;
}

/**
 * Coverage validation over months 1–12. A month is a `gap` if covered by 0 rows
 * and an `overlap` if covered by >1 row. `fullCover` is true iff there are no
 * gaps and no overlaps (every month used exactly once). Invalid rows
 * (toMonth < fromMonth) cover no months. The UI renders this as a non-blocking
 * warning.
 */
export function validateCoverage(rows) {
  const counts = {};
  for (let m = 1; m <= 12; m++) counts[m] = 0;
  for (const row of rows) {
    for (let m = row.fromMonth; m <= row.toMonth; m++) {
      if (m >= 1 && m <= 12) counts[m] += 1;
    }
  }
  const gaps = [];
  const overlaps = [];
  for (let m = 1; m <= 12; m++) {
    if (counts[m] === 0) gaps.push(m);
    else if (counts[m] > 1) overlaps.push(m);
  }
  return { fullCover: gaps.length === 0 && overlaps.length === 0, gaps, overlaps };
}

/**
 * Default calculator state: two annualEquivalent rows split mid-year
 * (Jan–Jun, Jul–Dec), values 0. Rounding is ON by default (see totalDays).
 */
export function defaultRows() {
  return [
    { fromMonth: 1, toMonth: 6, mode: ANNUAL_EQUIVALENT, value: 0 },
    { fromMonth: 7, toMonth: 12, mode: ANNUAL_EQUIVALENT, value: 0 },
  ];
}
